import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from '@tasama/i18n';
import { Text, DynamicRowsTable, type DynamicColumn } from '@tasama/ui';
import type {
  DynamicFieldType,
  FormColumnSchema,
  FormFieldSchema,
  FormSectionSchema,
  SchemaRule,
} from '@tasama/types';
import { resolveFieldState } from './conditions';
import { FIELD_REGISTRY, renderFieldControl } from './field-registry';
import { useDynamicFormRules } from './use-dynamic-form-rules.hook';
import {
  resolvePatternErrorMessage,
  resolveValidateRuleMessage,
} from './rule-evaluation.util';

// DynamicRowsTable only understands this fixed set — collapse the wider DynamicFieldType
// vocabulary onto it (e.g. datetime/lookupSelect/multiselect fall back to their closest match).
// yes_no has no radio/toggle equivalent in DynamicColumnType, so it falls back to checkbox too.
const COLUMN_TYPE_MAP: Partial<
  Record<DynamicFieldType, DynamicColumn['type']>
> = {
  date: 'date',
  datetime: 'date',
  checkbox: 'checkbox',
  yes_no: 'checkbox',
  select: 'select',
  lookupSelect: 'select',
  multiselect: 'select',
};

function toDynamicColumn(column: FormColumnSchema): DynamicColumn {
  return {
    key: column.name,
    label: column.label,
    type: COLUMN_TYPE_MAP[column.type] ?? 'text',
    ...(column.mandatory !== undefined ? { required: column.mandatory } : {}),
    // select/multiselect grid columns carry their options in `choices` (e.g. Overtime "Day");
    // the grid renders a Dropdown from `options`, so surface them here.
    ...(column.choices
      ? {
          options: column.choices.map((c) => ({
            value: c.value,
            label: c.label,
          })),
        }
      : {}),
  };
}

interface DynamicFormRendererProps {
  sections: FormSectionSchema[];
  rules: SchemaRule[];
  testID: string;
}

// Renderer over the real BFF schema shape: { sections[].fields[] } + the one top-level
// `staticRules[]` array (see form.types.ts CatalogItemSchema/SchemaRule). Fields render in a flat
// vertical list sorted by the BE-provided `order` (not array position), except for runs of fields
// carrying `column`, which came from a ServiceNow split container and render as two side-by-side
// stacks the way the Service Portal shows them (TDF-1422).
export function DynamicFormRenderer({
  sections,
  rules,
  testID,
}: DynamicFormRendererProps) {
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { choicesOverride } = useDynamicFormRules({
    control,
    setValue,
    rules,
  });
  const values = useWatch({ control }) as Record<string, unknown>;
  const { t } = useTranslation('portal');

  const renderField = (field: FormFieldSchema) => {
    // Fields whose type isn't in FIELD_REGISTRY are SNOW layout-only markers
    // (e.g. `container_split` formatters) — not real data fields, never rendered.
    if (!FIELD_REGISTRY[field.type]) return null;

    const state = resolveFieldState(field.name, rules, values);
    if (field.hidden || state.hidden) return null;

    const overriddenChoices = choicesOverride[field.name];

    // Blocking messages that RHF's formState.errors cannot be relied on to carry
    // (see resolveValidateRuleMessage). Evaluated on live values so the reason SUBMIT is
    // disabled appears next to the field the moment the user creates it. (TDF-1410 FE-2)
    const ruleErrorMessage =
      resolveValidateRuleMessage(field.name, rules, values) ??
      resolvePatternErrorMessage(
        field,
        values[field.name],
        t('services.activityRequest.validation.invalidValue')
      );

    return (
      <div key={field.name}>
        {renderFieldControl({
          field: {
            ...field,
            mandatory: field.mandatory || state.required,
            readOnly: field.readOnly || state.readOnly,
            label: state.label ?? field.label,
            ...(overriddenChoices
              ? {
                  choices: overriddenChoices.map((c) => ({
                    ...c,
                    sysId: null,
                  })),
                }
              : {}),
          },
          register,
          control,
          errors,
          t,
          ...(ruleErrorMessage ? { ruleErrorMessage } : {}),
          testID: `${testID}-${field.name}`,
        })}
      </div>
    );
  };

  const renderMultiRowSection = (section: FormSectionSchema) => {
    // A multi-row set is a section, so rules target its `key` rather than any field name. That
    // state has to be resolved here: `renderSection` hands MRVS sections straight to this
    // function and so never reaches the per-field `resolveFieldState` filter. Without it a grid
    // a rule has hidden stayed painted, e.g. Business Trip's Passenger Details showing while
    // Flight Required is unticked, where ServiceNow hides it. `collectMissingRequirements`
    // already resolves the same state for the summary panel (TDF-1410).
    if (resolveFieldState(section.key, rules, values).hidden) return null;

    const columns = (section.columns ?? []).map(toDynamicColumn);
    if (columns.length === 0) return null;

    return (
      <div key={section.key} className="space-y-4">
        <Text className="text-base font-semibold text-fg-primary">
          {section.label}
        </Text>
        <Controller
          name={section.key}
          control={control}
          defaultValue={[]}
          render={({ field: rhf }) => (
            <DynamicRowsTable
              columns={columns}
              value={(rhf.value as Record<string, unknown>[]) ?? []}
              onChange={rhf.onChange}
              addButtonLabel={t(
                'services.activityRequest.multiRow.addButtonLabel',
                { label: section.label }
              )}
              modalTitle={t('services.activityRequest.multiRow.modalTitle', {
                label: section.label,
              })}
              editModalTitle={t(
                'services.activityRequest.multiRow.editModalTitle',
                { label: section.label }
              )}
              emptyStateHeading={t(
                'services.activityRequest.multiRow.emptyStateHeading',
                { label: section.label }
              )}
              testID={`${testID}-${section.key}`}
            />
          )}
        />
      </div>
    );
  };

  const renderSection = (section: FormSectionSchema) => {
    if (section.multiRow) return renderMultiRowSection(section);

    const sortedFields = [...(section.fields ?? [])].sort(
      (a, b) => a.order - b.order
    );
    const visibleFields = sortedFields.filter((field) => {
      if (!FIELD_REGISTRY[field.type]) return false;
      const state = resolveFieldState(field.name, rules, values);
      return !field.hidden && !state.hidden;
    });
    if (visibleFields.length === 0) return null;

    // Split the section into alternating full-width and two-column blocks, preserving reading order.
    // Grouping by membership (not by row) means a conditionally hidden field just shortens its own
    // column instead of leaving a hole opposite the field it was paired with.
    const blocks: { grid: boolean; fields: FormFieldSchema[] }[] = [];
    for (const field of visibleFields) {
      const grid = field.column === 1 || field.column === 2;
      const current = blocks.at(-1);
      if (current?.grid === grid) current.fields.push(field);
      else blocks.push({ grid, fields: [field] });
    }

    return (
      <div key={section.key} className="space-y-4">
        <Text className="text-base font-semibold text-fg-primary">
          {section.label}
        </Text>
        {blocks.map((block, index) =>
          block.grid ? (
            <div
              key={`${section.key}-grid-${block.fields[0]?.name ?? index}`}
              className="grid grid-cols-1 gap-4 md:grid-cols-2"
              data-testid={`${testID}-${section.key}-grid`}
            >
              {([1, 2] as const).map((column) => (
                <div key={column} className="space-y-4">
                  {block.fields
                    .filter((field) => field.column === column)
                    .map((field) => renderField(field))}
                </div>
              ))}
            </div>
          ) : (
            block.fields.map((field) => renderField(field))
          )
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid={testID}>
      {sections.map((section) => renderSection(section))}
    </div>
  );
}
